import type {
  AccountInfo,
  OrganizationInfo
} from '@src/endpoints/services/base.service'
import { normalize, schema } from 'normalizr'
import type {
  AccountsSchema,
  OrganizationEntities,
  Organizations,
  OrganizationsSchema,
  SingleAccountEntities,
  SingleAccounts,
  SubOrganizationsSchema
} from '../models'
import { forEach, groupBy, isEmpty, keys } from 'lodash'

export const mapSingleAccounts = (data: AccountInfo[]): SingleAccounts => {
  const accountSchema = new schema.Entity<AccountsSchema>(
    'accounts',
    {},
    { idAttribute: 'uid' }
  )
  const accountByIdSchema = new schema.Entity<AccountsSchema>(
    'accounts',
    {},
    { idAttribute: 'account_id' }
  )
  const accountListByUIDSchema = new schema.Array(accountSchema)
  const accountListByIDSchema = new schema.Array(accountByIdSchema)
  const { entities = {} as SingleAccountEntities, result = [] } = normalize<
    any,
    SingleAccountEntities,
    string[]
  >(data || [], accountListByUIDSchema)

  const { entities: entitiesByID = {} as SingleAccountEntities } = normalize<
    any,
    SingleAccountEntities,
    string[]
  >(data || [], accountListByIDSchema)

  return {
    entities: {
      ...entities,
      accountsByAccountId: entitiesByID?.accounts || {}
    },
    accountUIDs: result,
    accountIds: keys(entitiesByID?.accounts)
  }
}

export const mapOrganizations = (data: OrganizationInfo[]): Organizations => {
  const accountByIDSchema = new schema.Entity<AccountsSchema>(
    'accountsByAccountId',
    {},
    {
      idAttribute: 'account_id',
      processStrategy: x => ({ ...x, aid: x.account_id })
    }
  )
  const accountSchema = new schema.Entity<AccountsSchema>(
    'accounts',
    {},
    {
      idAttribute: 'uid',
      processStrategy: x => ({ ...x, aid: x.account_id })
    }
  )

  const subOrgSchema = new schema.Entity<SubOrganizationsSchema>(
    'subOrganizations',
    {
      account_info: [accountSchema],
      aids: [accountByIDSchema]
    },
    {
      idAttribute: 'suborganization_uid',
      processStrategy: x => ({ ...x, aids: x.account_info })
    }
  )
  subOrgSchema.define({
    sub_organization: [subOrgSchema],
    account_info: [accountSchema],
    aids: [accountByIDSchema]
  })

  const orgSchema = new schema.Entity<OrganizationsSchema>(
    'organizations',
    {
      account_info: [accountSchema],
      sub_organization: [subOrgSchema],
      aids: [accountByIDSchema]
    },
    {
      idAttribute: 'organization_uid',
      processStrategy: x => ({
        ...x,
        aids: x.account_info
      })
    }
  )

  const listSchema = new schema.Array(orgSchema)

  const { entities = {} as OrganizationEntities, result = [] } = normalize<
    any,
    OrganizationEntities,
    string[]
  >(data || [], listSchema)

  const organizationsByOrgId = {}
  const subOrganizationsById = {}
  forEach(entities?.organizations || {}, org => {
    if (!isEmpty(org?.organization_id)) {
      organizationsByOrgId[org?.organization_id] = org
    }
  })

  const accountsByOrgUID = groupBy(entities.accounts, x => x.root_uid) || {}

  forEach(entities?.subOrganizations || {}, subOrg => {
    if (!isEmpty(subOrg?.suborganization_id)) {
      subOrganizationsById[subOrg.suborganization_id] = subOrg
    }
  })

  return {
    entities: {
      ...entities,
      organizationsByOrgId,
      subOrganizationsById,
      accountsByOrgUID
    },
    organizationUIDs: result,
    organizationIds: keys(organizationsByOrgId),
    subOrganizationsUIDs: keys(entities?.subOrganizations),
    subOrganizationsIds: keys(subOrganizationsById),
    accountIds: keys(entities?.accountsByAccountId),
    accountUIDs: keys(entities?.accounts)
  }
}
